import { Fragment, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { IconType } from 'react-icons';
import {
  FiAward,
  FiBell,
  FiCheck,
  FiLink,
  FiMapPin,
  FiShield,
  FiStar,
  FiX,
  FiZap,
} from 'react-icons/fi';
import { createInNetworkCheckoutSession } from '@worker/services/MyJobs/api';
import { useWorkerStore } from '@worker/stores';
import { BASE_PATH } from '@utils/Environment';

const COLORS = {
  dark: '#1C1B18',
  gold: '#F4C430',
  cream: '#F7F5EF',
  iconBg: '#FBF3D9',
  bodyText: '#6B6858',
  muted: '#A8A599',
  alsoIncluded: '#948F7D',
  border: '#DEDACB',
  closeBg: 'rgba(255,255,255,0.08)',
  closeIcon: '#E8E6DF',
  iconRing: '#4A4638',
  gradientFrom: '#2A2823',
};

const FEATURES: { Icon: IconType; title: string; description: string }[] = [
  {
    Icon: FiZap,
    title: 'Instant Suggested Worker in local jobs',
    description:
      'The moment a company creates a local job that matches your personal My Jobs settings, you appear!',
  },
  {
    Icon: FiStar,
    title: 'Top of every list',
    description:
      'List above other workers when companies search local talent, or you apply.',
  },
  {
    Icon: FiBell,
    title: 'Get new job alerts faster',
    description:
      'Find out and apply before the single Trusted Herd daily notification goes out to everyone.',
  },
  {
    Icon: FiMapPin,
    title: 'Add locations and expand your radius',
    description:
      'Add up to three extra locations, alongside your home address and expand new job alerts from 35 to 100 miles.',
  },
];

const ALSO_INCLUDED: { Icon: IconType; text: string }[] = [
  { Icon: FiShield, text: 'Verified In-Network badge on profile' },
  { Icon: FiLink, text: 'Pick your custom TH profile URL' },
];

const DISCLAIMER =
  "You'll lose In-Network status if a company marks you No-Call or No-Show.";

const MY_JOBS_PATH = `${BASE_PATH}jobs/my-jobs/new`;

export interface GetInNetworkProps {
  onJoin: () => void;
  onClose: () => void;
}

function CtaBlock({
  buttonVariant,
  label,
  isLoading,
  onClick,
}: {
  buttonVariant: 'brand' | 'negative-brand';
  label: string;
  isLoading: boolean;
  onClick: () => void;
}) {
  return (
    <Stack gap={12} mt={buttonVariant === 'brand' ? 0 : 'xl'}>
      <Button
        fullWidth
        size="md"
        radius="md"
        variant={buttonVariant}
        loading={isLoading}
        onClick={onClick}
      >
        {label}
      </Button>
      <Text size="xs" c={COLORS.alsoIncluded} ta="center" lh={1.5}>
        {DISCLAIMER}
      </Text>
    </Stack>
  );
}

export default function GetInNetwork({ onJoin, onClose }: GetInNetworkProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { isInNetwork } = useWorkerStore();
  const isPremium = isInNetwork();
  const navigate = useNavigate();
  const location = useLocation();

  const ctaLabel = isPremium ? 'GO TO MY JOBS' : 'JOIN IN-NETWORK';
  const HeroIcon = isPremium ? FiCheck : FiAward;

  const handleJoinClick = async () => {
    try {
      setIsLoading(true);
      const { sessionUrl } = await createInNetworkCheckoutSession();

      window.location.href = sessionUrl;
      onJoin();
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to start checkout process. Please try again.',
        color: 'red',
      });
      setIsLoading(false);
    }
  };

  const handleGoToMyJobs = () => {
    onClose();
    if (!location.pathname.includes('jobs/my-jobs')) {
      navigate(MY_JOBS_PATH);
    }
  };

  const handleCtaClick = isPremium ? handleGoToMyJobs : handleJoinClick;

  return (
    <Box mah="90vh" style={{ overflowY: 'auto' }}>
      <Flex direction={{ base: 'column-reverse', md: 'row' }} align="stretch">
        <Box
          px={{ base: 24, md: 36 }}
          py={{ base: 28, md: 32 }}
          flex={1.15}
          bg={COLORS.cream}
        >
          <Stack gap={0}>
            {FEATURES.map(({ Icon, title, description }, index) => (
              <Fragment key={title}>
                <Flex gap={14} py={14}>
                  <Flex
                    w={40}
                    h={40}
                    miw={40}
                    align="center"
                    justify="center"
                    bdrs={10}
                    bg={COLORS.iconBg}
                  >
                    <Icon size={20} color={COLORS.dark} />
                  </Flex>
                  <Box>
                    <Text fw={500} size="md" c={COLORS.dark} mb={3} lh={1.3}>
                      {title}
                    </Text>
                    <Text size="sm" c={COLORS.bodyText} lh={1.5}>
                      {description}
                    </Text>
                  </Box>
                </Flex>
                {index < FEATURES.length - 1 && (
                  <Divider color={COLORS.border} />
                )}
              </Fragment>
            ))}

            <Divider color={COLORS.border} mt={4} />
            <Text
              size="xs"
              fw={500}
              tt="uppercase"
              lts="0.08em"
              c={COLORS.alsoIncluded}
              pt={14}
            >
              Also included
            </Text>

            <SimpleGrid cols={2} spacing={10} mt={10}>
              {ALSO_INCLUDED.map(({ Icon, text }) => (
                <Flex
                  key={text}
                  gap={10}
                  p={12}
                  align="center"
                  bg={COLORS.iconBg}
                  bdrs={10}
                >
                  <Icon size={18} color={COLORS.dark} />
                  <Text size="sm" fw={500} c={COLORS.dark} lh={1.35}>
                    {text}
                  </Text>
                </Flex>
              ))}
            </SimpleGrid>
          </Stack>

          <Box display={{ base: 'block', md: 'none' }}>
            <CtaBlock
              buttonVariant="negative-brand"
              label={ctaLabel}
              isLoading={isLoading}
              onClick={handleCtaClick}
            />
          </Box>
        </Box>

        <Box
          pos="relative"
          w={{ base: '100%', md: '42%' }}
          px={{ base: 36, md: 32 }}
          pt={{ base: 44, md: 48 }}
          pb={32}
          style={{
            background: `radial-gradient(ellipse at 50% 0%, ${COLORS.gradientFrom} 0%, ${COLORS.dark} 70%)`,
          }}
        >
          <ActionIcon
            aria-label="Close"
            variant="transparent"
            onClick={onClose}
            pos="absolute"
            top={20}
            right={20}
            w={30}
            h={30}
            bdrs="50%"
            bg={COLORS.closeBg}
          >
            <FiX size={16} color={COLORS.closeIcon} />
          </ActionIcon>

          <Stack h="100%" justify="center" ta="center" gap={40}>
            <Stack gap={10} align="center">
              <Flex
                w={76}
                h={76}
                align="center"
                justify="center"
                bdrs="50%"
                bg={COLORS.gold}
                bd={`3px solid ${COLORS.iconRing}`}
              >
                <HeroIcon size={38} color={COLORS.dark} />
              </Flex>
              <Text
                size="xs"
                fw={500}
                tt="uppercase"
                lts="0.12em"
                c={COLORS.gold}
                mt={6}
              >
                {isPremium ? "YOU'RE IN-NETWORK" : 'JOIN IN-NETWORK'}
              </Text>
              <Title order={2} c={COLORS.cream} fw={500} lh={1.25} fz={28}>
                {isPremium ? "You're being seen first" : 'Be seen first'}
              </Title>
              <Text size="md" c={COLORS.muted} lh={1.6} maw={360}>
                {isPremium
                  ? 'You have access to premium features that make you more visible and desirable to companies.'
                  : "Show companies you're committed and become more desirable for opportunities."}
              </Text>
            </Stack>

            <Box display={{ base: 'none', md: 'block' }}>
              <CtaBlock
                buttonVariant="brand"
                label={ctaLabel}
                isLoading={isLoading}
                onClick={handleCtaClick}
              />
            </Box>
          </Stack>
        </Box>
      </Flex>
    </Box>
  );
}
